import AntIcon from "@/components/icons/AntIcon";
import PopconfirmDeleteIcon from "@/components/icons/PopconfirmDeleteIcon";
import { useWindowSize } from "@/utils/hooksUtils";
import { numberOrZero } from "@/utils/utils";
import { Button, Flex, Popconfirm, Popover, Progress } from "antd";
import { useState } from "react";
import CountUp from "react-countup";
import { useTranslation } from "react-i18next";
import { useDeleteDashboardGoal } from "../../../../../../common/api/queries";
import { Permission } from "../../../../../../common/security/enums";
import { UUID } from "../../../../../../typings/global";
import { useHasPermissions } from "../../../../../auth/hooks";
import { DashboardGoal } from "../../../../types";
import { DASHBOARD_LARGER_SCREEN_UPDATE_WIDTH, DASHBOARD_MIDLLE_SCREEN_UPDATE_WIDTH } from "../../../../utils";
import { UpdateGoalModal } from "../modals/UpdateGoalModal";
import { isCreatedGoal } from "../utils";

type Props = {
  label: React.ReactNode;
  percentage: number;
  hasTarget: boolean;
  targetLabel?: React.ReactNode;
  isGaugeStyle?: boolean;
  goal?: DashboardGoal;
  agentId: UUID;
  year: number;
  tooltipTitle?: React.ReactNode;
};

const getGoalSize = (windowSize: number) => {
  if (windowSize >= DASHBOARD_LARGER_SCREEN_UPDATE_WIDTH) {
    return 180;
  } else if (windowSize >= DASHBOARD_MIDLLE_SCREEN_UPDATE_WIDTH) {
    return 160;
  } else {
    return 150;
  }
};

export const GoalCard = ({
  percentage,
  label,
  isGaugeStyle,
  hasTarget,
  targetLabel,
  agentId,
  goal,
  year,
  tooltipTitle,
}: Props) => {
  const { t } = useTranslation();

  const windowSize = useWindowSize();

  const [isOpenEditGoalModal, setIsOpenEditGoalModal] = useState(false);

  const hasAdminDashboardGoalsPermission = useHasPermissions(Permission.ADMIN_DASHBOARD_GOALS);

  const deleteGoal = useDeleteDashboardGoal();

  const isGoalCompleted = percentage >= 100;

  return (
    <Flex
      vertical
      align="center"
      style={{ backgroundColor: "#00152a", height: "100%", borderRadius: 18, color: "white" }}
    >
      {hasAdminDashboardGoalsPermission && isCreatedGoal(goal) ? (
        <>
          <Button
            title={t("dashboard.goals.titles.updateGoal")}
            style={{ position: "absolute", top: 16, left: 28 }}
            color="gold"
            size="small"
            variant="text"
            icon={<AntIcon type="edit" size="large" />}
            onClick={() => setIsOpenEditGoalModal(true)}
          />

          <Popconfirm
            title={t("dashboard.goals.actions.deleteConfirm")}
            icon={<PopconfirmDeleteIcon />}
            cancelText={t("common.no")}
            okText={t("common.yes")}
            okType="danger"
            onConfirm={() => deleteGoal.mutate({ id1: agentId, id2: goal.id })}
          >
            <Button
              style={{ position: "absolute", top: 16, right: 28, paddingTop: 2 }}
              color="gold"
              size="small"
              variant="text"
              icon={<AntIcon type="delete" color="yellow" size="large" />}
            />
          </Popconfirm>
        </>
      ) : undefined}

      <div style={{ margin: "50px 24px 24px" }}>
        <Popover content={isGoalCompleted ? t("dashboard.goals.helpers.goalCompletedHint") : tooltipTitle}>
          <Progress
            style={{ color: "white" }}
            percent={hasTarget ? percentage : 0}
            type={isGaugeStyle ? "dashboard" : "circle"}
            strokeColor={"#ffce2b"}
            strokeWidth={10}
            size={getGoalSize(windowSize.innerWidth)}
            railColor={"#dddddd"}
            format={(value) => {
              if (percentage >= 100) {
                return <AntIcon style={{ fontSize: 54 }} type="check" color="yellow" />;
              }

              if (targetLabel) {
                return targetLabel;
              }

              return (
                <div style={{ color: "white", fontSize: 16 }}>
                  <span style={{ fontSize: 42, fontWeight: 600 }}>
                    <CountUp duration={0.5} end={numberOrZero(value)} />
                  </span>
                  <span>%</span>
                </div>
              );
            }}
          />
        </Popover>
      </div>

      <div style={{ color: "white", fontWeight: 600, fontSize: 24, textAlign: "center", padding: "0 12px" }}>
        {label}
      </div>

      {isCreatedGoal(goal) ? (
        <UpdateGoalModal
          goal={goal}
          isOpen={isOpenEditGoalModal}
          year={year}
          agentId={agentId}
          onCancel={() => setIsOpenEditGoalModal(false)}
        />
      ) : undefined}
    </Flex>
  );
};
